import { ThunkDispatch } from "@reduxjs/toolkit";
import React from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { itemRemovalActions } from "../../reducers/itemRemoval";
import { selectCanProcessItemRemoval } from "../../selectors";
import ExamplePhotoNG from "../ExamplePhotoNG";
import { BlankPhotoIcon, SelectFileIcon, GenerateAfterPhotoIcon } from "../SVGIcons";

// State型定義
interface RootState {
  itemRemoval: {
    isLoading: boolean;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

// mapStateToPropsの型定義
function mapStateToProps(state: RootState) {
  const canProcessItemRemoval = selectCanProcessItemRemoval(state);
  return { ...state.itemRemoval, canProcessItemRemoval };
}

// コンポーネントのProps型定義
interface ItemRemovalEditFormEmptyProps {
  isLoading: boolean;
  canProcessItemRemoval?: boolean;
  stubId: string;
}

// ドラッグ＆ドロップイベントの型定義
interface DragEvent extends React.DragEvent<HTMLLabelElement> {
  dataTransfer: DataTransfer;
}

const ItemRemovalEditFormEmpty = (props: ItemRemovalEditFormEmptyProps) => {
  const { isLoading, stubId } = props;
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const navigate = useNavigate();

  const addItemImages = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isLoading || !event.target.files) return;
    const formData = {
      stubId: stubId.toString(),
      files: event.target.files
    };
    dispatch(itemRemovalActions.addItemImages(formData));
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    if (isLoading) return;

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFiles = e.dataTransfer.files;
      const formData = {
        stubId: stubId.toString(),
        files: droppedFiles
      };
      dispatch(itemRemovalActions.addItemImages(formData));
    }
  };

  return (
    <div className="max-w-8xl w-full mx-auto pt-1 pb-3 px-4 sm:px-6 md:px-8 text-[#2d2d2d]">
      <div className="grid grid-cols-9 gap-2">
        <div className="col-1 bg-gray-200 overflow-y-auto rounded min-h-[30.28rem] flex flex-col p-2">
          <ul className="space-y-2">
            {[...Array(5)].map((_, index) => (
              <li
                key={index}
                className="flex items-center justify-center w-full bg-gray-300 h-24 w-28 overflow-hidden mx-auto rounded-sm"
              >
                <BlankPhotoIcon className="w-10 h-10 text-gray-400" />
              </li>
            ))}
          </ul>
        </div>

        <div className="grid grid-cols-2 col-span-8 border border-gray-300 bg-gray-100 overflow-y-auto rounded">
          <div className="flex flex-col w-full col-1 flex-nowrap p-3 bg-white">
            <label
              className="flex flex-col items-center justify-center h-full max-h-[33rem] border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
              htmlFor="file-input"
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6 space-y-3">
                <SelectFileIcon className="w-12 h-12 text-gray-500" />
                <p className="mb-2 text-sm text-gray-500">
                  <strong className="font-semibold text-blue-600">
                    画像を選択、またはドラッグ＆ドロップ
                  </strong>
                </p>
                <p className="mb-2 text-sm text-gray-500">
                  <strong className="font-semibold text-blue-600">最大5枚まで入力可能です</strong>
                </p>
                <p className="text-xs text-gray-500">
                  image/jpeg, image/png, image/webpが使用可能（5MBまで）
                </p>
                <div className="px-3 py-2 focus:ring-blue-500 focus:border-blue-500 whitespace-nowrap text-sm font-medium rounded-md text-white hover:text-white bg-blue-600 hover:bg-blue-700 w-auto min-w-28 text-center">
                  画像を選択
                </div>
              </div>
              <input
                accept="image/png, image/jpeg, image/webp"
                aria-label="画像ファイルを選択"
                className="hidden"
                id="file-input"
                multiple
                type="file"
                onChange={addItemImages}
              />
            </label>
          </div>

          <ExamplePhotoNG tabName="itemRemoval" />
        </div>
      </div>
      <div className="grid grid-cols-9 gap-4 mt-3">
        <div className="col-span-4 col-start-2 space-y-2">
          <button
            className="px-5 py-2.5 flex items-center gap-1 rounded-full mx-auto text-sm text-gray-900 bg-gray-400 disabled:opacity-75"
            disabled
            type="button"
          >
            <GenerateAfterPhotoIcon />
            AI家具消しを実行
          </button>
        </div>
      </div>
    </div>
  );
};

export default connect(mapStateToProps)(ItemRemovalEditFormEmpty);